/* ==========================================================================
   Tomato Superstar - header + global navigation behaviour (redesign v1.0)

   Deliberately additive. It does NOT re-implement anything the store's own
   theme.js already owns:
     mini cart, cart count, free-shipping meter  -> theme.miniCart / theme.freeShipping
     predictive search results                   -> theme.ajaxSearch (.js-ajaxsearch)
     sign-in / compare modals                    -> Bootstrap data-toggle
     wishlist + compare counts                   -> theme.wishlist / theme.compare
     drawer open / close / focus trap            -> window.Drawers ("NavDrawer", "left")
     sticky-header .active class                 -> theme.stickyHeader

   This file adds only: mega-menu open/close (pointer + keyboard), the account
   dropdown, the search overlay, and the mobile drawer's panel drill-down.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP_NAV = '(min-width: 990px)';
  var isDesktop = function () {
    return window.matchMedia && window.matchMedia(DESKTOP_NAV).matches;
  };

  /* ---------------------------------------------------------------- mega menu */
  function initMegaMenu() {
    var header = document.querySelector('.tss-headroom');
    var items = [].slice.call(document.querySelectorAll('[data-tss-menu]'));
    if (!header || !items.length) return;

    var openItem = null;
    var closeTimer = null;

    function open(item) {
      if (openItem === item) return;
      if (openItem) setState(openItem, false);
      setState(item, true);
      openItem = item;
      header.classList.add('has-open-menu');
    }

    function close() {
      if (!openItem) return;
      setState(openItem, false);
      openItem = null;
      header.classList.remove('has-open-menu');
    }

    function setState(item, isOpen) {
      item.classList.toggle('is-open', isOpen);
      var link = item.querySelector('.tss-nav__link');
      if (link) link.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    function cancelClose() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function scheduleClose() {
      cancelClose();
      // short grace period so a diagonal cursor path to the panel doesn't close it
      closeTimer = setTimeout(close, 140);
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        cancelClose();
        open(item);
      });

      item.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        scheduleClose();
      });

      // keyboard: the chevron link opens the panel on Enter/Space instead of
      // navigating, matching how sighted users experience the hover panel.
      var link = item.querySelector('.tss-nav__link');
      if (!link) return;

      link.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          if (openItem !== item) {
            event.preventDefault();
            cancelClose();
            open(item);
            var first = item.querySelector('.tss-mega a');
            if (first) first.focus();
          }
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          cancelClose();
          open(item);
          var target = item.querySelector('.tss-mega a');
          if (target) target.focus();
        }
      });

      item.addEventListener('focusin', function () {
        if (!isDesktop()) return;
        cancelClose();
        open(item);
      });

      item.addEventListener('focusout', function (event) {
        if (!isDesktop()) return;
        if (!item.contains(event.relatedTarget)) scheduleClose();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !openItem) return;
      var link = openItem.querySelector('.tss-nav__link');
      close();
      if (link) link.focus();
    });

    var scrim = document.querySelector('[data-tss-scrim]');
    if (scrim) scrim.addEventListener('click', close);

    // a resize into mobile must not leave a panel stranded open
    window.addEventListener('resize', function () {
      if (!isDesktop()) close();
    });
  }

  /* ----------------------------------------------------------- account menu */
  function initAccountMenu() {
    var root = document.querySelector('[data-tss-account]');
    if (!root) return;

    var button = root.querySelector('button');
    if (!button) return;

    function setOpen(isOpen) {
      root.classList.toggle('is-open', isOpen);
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    button.addEventListener('click', function (event) {
      event.stopPropagation();
      setOpen(!root.classList.contains('is-open'));
    });

    root.addEventListener('mouseenter', function () {
      if (isDesktop()) setOpen(true);
    });

    root.addEventListener('mouseleave', function () {
      if (isDesktop()) setOpen(false);
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) setOpen(false);
    });

    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) setOpen(false);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-open')) {
        setOpen(false);
        button.focus();
      }
    });

    // clicking Sign In / Compare hands over to a Bootstrap modal - get out of its way
    [].slice.call(root.querySelectorAll('[data-toggle="modal"]')).forEach(function (link) {
      link.addEventListener('click', function () { setOpen(false); });
    });
  }

  /* --------------------------------------------------------- search overlay */
  function initSearch() {
    var overlay = document.querySelector('[data-tss-search]');
    if (!overlay) return;

    var input = overlay.querySelector('input[name="q"]');
    var lastTrigger = null;

    function open(trigger) {
      lastTrigger = trigger || null;
      overlay.hidden = false;
      // next frame, so the transition has a start value to animate from
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        if (input) {
          input.focus();
          input.select();
        }
      });
      document.documentElement.classList.add('tss-no-scroll');
      syncTriggers(true);
    }

    function close() {
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('tss-no-scroll');
      syncTriggers(false);
      window.setTimeout(function () {
        if (!overlay.classList.contains('is-open')) overlay.hidden = true;
      }, 320);
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    }

    function syncTriggers(isOpen) {
      [].slice.call(document.querySelectorAll('[data-tss-search-open]')).forEach(function (el) {
        if (el.hasAttribute('aria-expanded')) {
          el.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
      });
    }

    [].slice.call(document.querySelectorAll('[data-tss-search-open]')).forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        // opened from inside the drawer: shut the drawer first
        if (window.theme && theme.NavDrawer && trigger.closest('#NavDrawer')) {
          theme.NavDrawer.close();
        }
        open(trigger);
      });
    });

    [].slice.call(overlay.querySelectorAll('[data-tss-search-close]')).forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    // keep focus inside the sheet while it is open
    overlay.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !overlay.classList.contains('is-open')) return;
      var focusable = [].slice.call(overlay.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"])'
      )).filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ------------------------------------------------- mobile drawer drilldown */
  function initDrawerPanels() {
    var drawer = document.querySelector('[data-tss-drawer]');
    if (!drawer) return;

    var panels = {};
    [].slice.call(drawer.querySelectorAll('[data-tss-panel-id]')).forEach(function (panel) {
      panels[panel.getAttribute('data-tss-panel-id')] = panel;
    });

    var current = 'root';

    function go(id, direction) {
      var next = panels[id];
      if (!next || id === current) return;

      var previous = panels[current];
      if (previous) {
        previous.classList.remove('is-active');
        previous.classList.toggle('is-behind', direction === 'forward');
      }

      next.classList.remove('is-behind');
      next.classList.add('is-active');
      next.scrollTop = 0;
      current = id;

      var focusTarget = next.querySelector('.tss-panel__back') || next.querySelector('.tss-panel__link');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    drawer.addEventListener('click', function (event) {
      var forward = event.target.closest('[data-tss-to]');
      if (forward) {
        event.preventDefault();
        go(forward.getAttribute('data-tss-to'), 'forward');
        return;
      }

      var back = event.target.closest('[data-tss-back]');
      if (back) {
        event.preventDefault();
        go(back.getAttribute('data-tss-back'), 'back');
      }
    });

    // reset to the top level whenever the drawer is closed, so the next open
    // never resumes three levels deep
    function reset() {
      if (current === 'root') return;
      Object.keys(panels).forEach(function (id) {
        panels[id].classList.remove('is-active', 'is-behind');
      });
      panels.root.classList.add('is-active');
      panels.root.scrollTop = 0;
      current = 'root';
    }

    var closeButton = drawer.querySelector('.js-drawer-close');
    if (closeButton) closeButton.addEventListener('click', function () { setTimeout(reset, 380); });

    // window.Drawers closes on a page click too, so watch the class it toggles
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!document.documentElement.classList.contains('js-drawer-open-left')) {
          setTimeout(reset, 380);
        }
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    // links inside the drawer close it before navigating so the back button
    // never returns to a page with the drawer still open
    drawer.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link || link.getAttribute('href').indexOf('javascript:') === 0) return;
      if (window.theme && theme.NavDrawer) theme.NavDrawer.close();
    });
  }

  /* ------------------------------------------------------------- count chips */
  /* theme.wishlist / theme.compare / theme.miniCart write a plain number into
     these nodes, including "0" on an empty list. Flag the zero state so CSS can
     hide it, and keep watching because those scripts rewrite the text later. */
  function initCountChips() {
    var chips = [].slice.call(document.querySelectorAll('.tss-count'));
    if (!chips.length) return;

    function sync() {
      chips.forEach(function (chip) {
        var value = (chip.textContent || '').trim();
        chip.classList.toggle('is-zero', value === '' || value === '0');
      });
    }

    sync();

    if (window.MutationObserver) {
      var observer = new MutationObserver(sync);
      chips.forEach(function (chip) {
        observer.observe(chip, { childList: true, characterData: true, subtree: true });
      });
    }

    // theme.js populates the counts on its own schedule after load
    window.setTimeout(sync, 400);
    window.setTimeout(sync, 1500);
  }

  function init() {
    initMegaMenu();
    initCountChips();
    initAccountMenu();
    initSearch();
    initDrawerPanels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The Theme Editor replaces the whole section on every settings change, which
     throws away the listeners bound above. Re-bind when the header section is
     reloaded so the mega menus, the drawer and the search sheet keep working
     while the merchant is editing. */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('.tss-headroom')) {
      init();
    }
  });
}());
